/**
 * Source system dropdown for the import preview step
 *
 * Lets users confirm or override the detected import source.
 * All ImportSource values except 'manual' are available, plus an
 * "Unknown / Not sure" option that maps to 'unknown'.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';
import { IoInformationCircle } from 'react-icons/io5';

import { SOURCE_LABELS } from '@/lib/cardUtils';
import type { ImportSource } from '@/types/card';

// ============================================================================
// Types
// ============================================================================

export interface SourceSystemDropdownProps {
  /** Currently selected source */
  value: ImportSource;
  /** Called when the source changes */
  onChange: (source: ImportSource) => void;
  /** Source detected by format auto-detection (shown as "Auto-detected" label) */
  detectedSource: ImportSource | null;
  /** Additional className */
  className?: string;
}

// Sources available in the dropdown (excludes 'manual')
const DROPDOWN_SOURCES: ImportSource[] = ['dragonshield', 'delverlens', 'moxfield', 'archidekt', 'manabox'];

// ============================================================================
// Component
// ============================================================================

export default function SourceSystemDropdown({ value, onChange, detectedSource, className }: SourceSystemDropdownProps): ReactElement {
  const isAutoDetected = detectedSource !== null && value === detectedSource;

  return (
    <div className={clsx('space-y-3', className)}>
      <div className="flex items-center justify-between">
        <label htmlFor="source-system" className="heading text-pearl text-sm font-medium">
          Import Source
        </label>
        {isAutoDetected && (
          <span className="text-aether flex items-center gap-1 text-xs">
            <IoInformationCircle className="h-3.5 w-3.5" />
            Auto-detected
          </span>
        )}
      </div>

      <select
        id="source-system"
        value={value}
        onChange={(e) => onChange(e.target.value as ImportSource)}
        className="bg-slate border-storm text-pearl focus-ring w-full appearance-none rounded-lg border py-2 pr-10 pl-3 text-sm"
      >
        <option value="unknown">Unknown / Not sure</option>
        {DROPDOWN_SOURCES.map((source) => (
          <option key={source} value={source}>
            {SOURCE_LABELS[source]}
          </option>
        ))}
      </select>

      <p className="text-ash text-xs">Tags imported cards with this source for future re-import and overwrite tracking.</p>
    </div>
  );
}
